import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Components
import Home from "./Home";
import Login from "./Login";
import AwbIn from "./AwbIn";
import AdminPanel from "./AdminPanel";
import CreateBill from "./CreateBill";
import { API_BASE_URL } from "./apiConfig";
import CreateBillWithSp from "./CreateBillWithSp";
import Employee from "./Employee";
import Account from "./Acm";
import PartyTransaction from "./PartyTransaction";
import PartyMaster from "./PartyMaster";
import ProductMaster from "./ProductMaster";
import CreateUser from "./CreateUser";
import ReportsHome from "./reports/ReportsHome";
import RoleManagement from "./RoleManagement";
import ProtectedRoute from "./ProtectedRoute";

export const API = {
  awb: `${API_BASE_URL}/awb`,
  emp: `${API_BASE_URL}/emp`,
  acm: `${API_BASE_URL}/acm`,
  user: `${API_BASE_URL}/user`,
};

function App() {
  const [appData, setAppData] = useState({
    emp: [],
    acm: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        const [awbRes, empRes, acmRes, userRes] = await Promise.all([
          fetch(API.awb),
          fetch(API.emp),
          fetch(API.acm),
          fetch(API.user),
        ]);

        if (!awbRes.ok || !empRes.ok || !acmRes.ok) {
          throw new Error("One or more API endpoints failed to respond correctly.");
        }

        const [awbData, empData, acmData, userData] = await Promise.all([
          awbRes.json(),
          empRes.json(),
          acmRes.json(),
          userRes.json(),
        ]);

        setAppData({
          awb: awbData,
          emp: empData,
          acm: acmData,
          user: userData,
        });

        setError(null);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError("Unable to connect to the server. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: "4rem", textAlign: "center", fontSize: "1.2rem", color: "#64748b" }}>
        Loading secure dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "4rem", textAlign: "center", color: "#dc2626" }}>
        <h2>Service Unavailable</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPanel awbData={appData.awb} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/CreateBill"
        element={
          <ProtectedRoute screenKey="party-transaction">
            <CreateBill awbData={appData.awb} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/CreateBillWithSp"
        element={
          <ProtectedRoute screenKey="party-transaction">
            <CreateBillWithSp awbData={appData.awb} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/Employee"
        element={
          <ProtectedRoute screenKey="employee-master">
            <Employee empData={appData.emp} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/Account"
        element={
          <ProtectedRoute screenKey="account-management">
            <Account acmData={appData.acm} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/party-master"
        element={
          <ProtectedRoute screenKey="party-master">
            <PartyMaster />
          </ProtectedRoute>
        }
      />

      <Route
        path="/product-master"
        element={
          <ProtectedRoute screenKey="product-master">
            <ProductMaster />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/partytransaction"
        element={
          <ProtectedRoute screenKey="party-transaction">
            <PartyTransaction />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/CreateUser"
        element={
          <ProtectedRoute screenKey="user-management">
            <CreateUser userData={appData.user} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute screenKey="reports">
            <ReportsHome />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/AwbIn"
        element={
          <ProtectedRoute screenKey="awb-in">
            <AwbIn />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/RoleManagement"
        element={
          <ProtectedRoute screenKey="role-management">
            <RoleManagement />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;